import { Button, TextInput } from '@utilitywarehouse/hearth-react';
import { Formik, useFormikContext } from 'formik';
import { ChangeEvent, useEffect } from 'react';

import { Dialog, DialogActions, DialogID } from '@components/Dialog';
import { ValidationErrorMessages } from '@constants/forms';
import { validatePostcode } from '@utils/validators';

interface VerifyModalProps {
  error: Nullable<string>;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (postcode: string) => void;
  resetError: () => void;
}

interface FormProps {
  error: Nullable<string>;
  isLoading: boolean;
  resetError: () => void;
}

interface Errors {
  restoreVerifyPostcode?: string;
}

interface FormValues {
  restoreVerifyPostcode: string;
}

const validateForm = (values: FormValues) => {
  const errors: Errors = {};
  if (!values.restoreVerifyPostcode) {
    errors.restoreVerifyPostcode = ValidationErrorMessages.POSTCODE_REQUIRED;
  } else if (!validatePostcode(values.restoreVerifyPostcode)) {
    errors.restoreVerifyPostcode = ValidationErrorMessages.INVALID_POSTCODE;
  }

  return errors;
};

const Form = ({ isLoading, error, resetError }: FormProps) => {
  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    handleBlur,
    setFieldError,
    isValid,
  } = useFormikContext<FormValues>();

  useEffect(() => {
    if (error) {
      setFieldError('restoreVerifyPostcode', error);
    }
  }, [error, setFieldError]);

  const handlePostcodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange(event);
    resetError();
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextInput
        label="Postcode"
        name="restoreVerifyPostcode"
        id="restoreVerifyPostcode"
        autoComplete="postal-code"
        value={values.restoreVerifyPostcode}
        onChange={handlePostcodeChange}
        onBlur={handleBlur}
        validationStatus={errors.restoreVerifyPostcode ? 'invalid' : undefined}
        validationText={errors.restoreVerifyPostcode}
        required
      />

      <DialogActions>
        <Button
          type="submit"
          variant="solid"
          data-testid="submitVerificationButton"
          disabled={isLoading || !isValid}
          loading={isLoading}
        >
          Continue my order
        </Button>
      </DialogActions>
    </form>
  );
};

const VerifyModal = ({
  error,
  isLoading,
  onClose,
  onSubmit,
  resetError,
}: VerifyModalProps) => (
  <Dialog
    id={DialogID.RESTORE_VERIFY}
    heading="Enter your postcode"
    description="To continue, please confirm the postcode for your order."
    onClose={onClose}
    className="restoreVerify__Dialog"
    data-testid="verifyPostcodeModal"
    preventDismiss
    hideCloseButton
  >
    <Formik
      initialValues={{
        restoreVerifyPostcode: '',
      }}
      validate={validateForm}
      onSubmit={(values) => onSubmit(values.restoreVerifyPostcode)}
    >
      <Form error={error} isLoading={isLoading} resetError={resetError} />
    </Formik>
  </Dialog>
);

export type { VerifyModalProps };
export { VerifyModal };
